/**
 * Utilidades para generación de URLs de elecciones
 */

/**
 * Remover acentos y caracteres especiales
 */
function removeAccentsAndSpecialChars(str) {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s]+/g, "-")
    .toLowerCase();
}

/**
 * Generar URL válida para resultados electorales
 * @param {string} baseUrl - URL base 
 * @param {string} provincia - Nombre de la provincia
 * @param {string} district - Nombre del distrito
 * @param {string} code - Código electoral
 * @returns {string} URL formateada
 */
export const generateValidUrl = (baseUrl, provincia, district, code) => {
  const districtSlug = removeAccentsAndSpecialChars(district);
  const provinciaSlug = removeAccentsAndSpecialChars(provincia);

  let urlResp = provincia.toLocaleLowerCase() == "argentina"
    ? `${baseUrl}${districtSlug}_8_${code}.html`
    : districtSlug !== ""
    ? `${baseUrl}${provinciaSlug}-${districtSlug}_8_${code}.html`
    : `${baseUrl}${provinciaSlug}_8_${code}.html`;
  
  return urlResp;
};

/**
 * Generar URL válida para XML/Sitemap
 * @param {string} name - Nombre del distrito
 * @param {string} baseUrl - URL base
 * @param {string} provincia - Nombre de la provincia
 * @param {string} code - Código electoral
 * @returns {string} URL formateada
 */
export const generateValidUrlXml = (name, baseUrl, provincia, code) => {
  const districtSlug = removeAccentsAndSpecialChars(name);
  const provinciaSlug = removeAccentsAndSpecialChars(provincia);

  return `${baseUrl}resultados-${provinciaSlug ? `${provinciaSlug}-` : ''}${districtSlug}_8_${code}.html`;
};

/**
 * Generar URL de iframe según tipo de elección
 * @param {object} params - Parámetros de elección
 * @returns {string} URL del iframe
 */
export const generateIframeUrl = (params) => {
  const { electionInstance, typeElection, codeLetter, codeNumber } = params;
  
  const baseIframeUrl = `https://ecp.clarin.com/2023/${electionInstance}/index.html#/map`;
  const mapType = typeElection === "presidente" ? "president" : "governors";
  const locationPath = codeLetter && codeNumber 
    ? `${codeLetter}/${codeLetter}${codeNumber}` 
    : `${codeLetter}/_`;

  return `${baseIframeUrl}/${mapType}/${locationPath}`;
};

/**
 * Construir URL completa de nota electoral
 * @param {object} config - Configuración
 * @returns {string} URL completa
 */
export const buildElectionNoteUrl = (config) => {
  const { 
    baseUrl = '/elecciones',
    year,
    electionType,
    category,
    provincia,
    distrito,
    code
  } = config;

  let url = `${baseUrl}/${year}/${electionType}/${category}/resultados-`;
  
  if (provincia) {
    url += removeAccentsAndSpecialChars(provincia);
  }
  
  if (distrito) {
    url += `-${removeAccentsAndSpecialChars(distrito)}`;
  }
  
  url += `_8_${code}.html`;
  
  return url;
};

/**
 * Extraer parámetros de URL electoral
 * @param {string} url - URL a parsear
 * @returns {object|null} Objeto con parámetros extraídos
 */
export const parseElectionUrl = (url) => {
  const regex = /elecciones\/(\d{4})\/(generales|balotaje)\/(presidente|gobernador|legislativas|delegados)\/resultados-(.+)_8_([A-Z]{2}-[A-Z]\d{3})\.html/;
  const match = url.match(regex);
  
  if (!match) return null;
  
  return {
    year: match[1],
    electionType: match[2],
    category: match[3],
    location: match[4],
    code: match[5]
  };
};

/**
 * Normalizar slug
 * @param {string} text - Texto a normalizar
 * @returns {string} Slug normalizado
 */
export const slugify = (text) => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
};